import React, { createContext, useState, useContext, useEffect } from 'react';

const ContentContext = createContext();

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

const defaultContent = {
  hero: {
    slides: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80',
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80',
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80',
    ],
  },
  featured: {
    alps: {
      title: 'Alps Photo Tour',
      description: 'Experience the breathtaking beauty of the Alps through our exclusive photography tours.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
    },
    iceland: {
      title: 'Iceland Photo Tour',
      description: 'Capture the raw, untamed landscapes of Iceland in this immersive photography journey.',
      image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&q=80',
    },
  },
  portfolio: [
    {
      id: 1,
      title: 'Mountain Vista',
      category: 'Landscape',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
      large: false,
    },
    {
      id: 2,
      title: 'Forest Path',
      category: 'Nature',
      image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=80',
      large: false,
    },
    {
      id: 3,
      title: 'Desert Dunes',
      category: 'Landscape',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&q=80',
      large: false,
    },
    {
      id: 4,
      title: 'Portrait Study',
      category: 'Portrait',
      image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600&q=80',
      large: true,
    },
    {
      id: 5,
      title: 'Waterfall',
      category: 'Nature',
      image: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=600&q=80',
      large: false,
    },
    {
      id: 6,
      title: 'Coastal View',
      category: 'Landscape',
      image: 'https://images.unsplash.com/photo-1472214103451-9374bd1c798e?w=600&q=80',
      large: false,
    },
  ],
  journal: [
    {
      id: 1,
      title: 'Photography and Sharing: My Return to Leica Akademie',
      date: 'December 2024',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
    },
    {
      id: 2,
      title: 'Sonus Faber - Suprema',
      date: 'November 2024',
      image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=80',
    },
    {
      id: 3,
      title: 'Maserati GT Lifestyle',
      date: 'October 2024',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&q=80',
    },
  ],
  shop: {
    presets: {
      title: 'Presets',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&q=80',
    },
    prints: {
      title: 'Prints',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
    },
  },
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    // Load content from localStorage on mount
    const savedContent = localStorage.getItem('site_content');
    if (savedContent) {
      try {
        setContent(JSON.parse(savedContent));
      } catch (error) {
        console.error('Error loading content:', error);
      }
    }
  }, []);

  const updateContent = (section, data) => {
    const newContent = {
      ...content,
      [section]: data,
    };
    setContent(newContent);
    localStorage.setItem('site_content', JSON.stringify(newContent));
  };

  const updateHeroSlides = (slides) => {
    updateContent('hero', { ...content.hero, slides });
  };

  const updateFeatured = (key, data) => {
    updateContent('featured', {
      ...content.featured,
      [key]: data,
    });
  };

  const updatePortfolio = (items) => {
    updateContent('portfolio', items);
  };

  const addPortfolioItem = (item) => {
    const newId = Math.max(...content.portfolio.map((i) => i.id), 0) + 1;
    const newItem = { ...item, id: newId };
    updatePortfolio([...content.portfolio, newItem]);
  };

  const updatePortfolioItem = (id, updates) => {
    const updated = content.portfolio.map((item) =>
      item.id === id ? { ...item, ...updates } : item
    );
    updatePortfolio(updated);
  };

  const deletePortfolioItem = (id) => {
    const filtered = content.portfolio.filter((item) => item.id !== id);
    updatePortfolio(filtered);
  };

  const updateJournal = (items) => {
    updateContent('journal', items);
  };

  const addJournalEntry = (entry) => {
    const newId = Math.max(...content.journal.map((i) => i.id), 0) + 1;
    const newEntry = { ...entry, id: newId };
    updateJournal([...content.journal, newEntry]);
  };

  const updateJournalEntry = (id, updates) => {
    const updated = content.journal.map((entry) =>
      entry.id === id ? { ...entry, ...updates } : entry
    );
    updateJournal(updated);
  };

  const deleteJournalEntry = (id) => {
    const filtered = content.journal.filter((entry) => entry.id !== id);
    updateJournal(filtered);
  };

  const updateShop = (key, data) => {
    updateContent('shop', {
      ...content.shop,
      [key]: data,
    });
  };

  const uploadImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <ContentContext.Provider
      value={{
        content,
        updateContent,
        updateHeroSlides,
        updateFeatured,
        updatePortfolio,
        addPortfolioItem,
        updatePortfolioItem,
        deletePortfolioItem,
        updateJournal,
        addJournalEntry,
        updateJournalEntry,
        deleteJournalEntry,
        updateShop,
        uploadImage,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

